import React, { useEffect, useState, useImperativeHandle } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
// TODO: swap in design system components once we have them
import { Switch } from '@mui/material';
import { client, NotificationData, NotificationSettings as NotificationSettingsType, ErrorResponse } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { InitialsAvatar } from 'design-system/src/components/InitialsAvatar/InitialsAvatar';
import { Table, TableCell, TableContainer } from '../Table';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import InfoIcon from 'design-system/src/assets/icons/infoIcon.svg?react';
import { applySavedFilters } from '../../utils';
import { AuthContext } from '../../auth';

const CustomTableContainer = styled(TableContainer)`
  width: 100%;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--grayscale-100);

  &:last-child {
    border-bottom: none;
  }
`;

const CustomTableCell = styled(TableCell)`
  padding: 12px 0;
  vertical-align: middle;
`;

const SwitchColumnHeaderCell = styled(CustomTableCell)`
  // align with switches, which have their own padding
  padding-left: 12px;
`;

const switchStyling = {
  color: `var(--grayscale-300)`,

  // Below selectors override MUI default styles
  // target Checkbox styles
  '&.Mui-checked': {
    color: `var(--purple-700)`,
  },
  // target Switch styles
  // note: using !important as a quick solution,
  // for some reason Switch selectors are more specific
  '.Mui-checked': {
    color: `var(--purple-700) !important`,
  },
  '.Mui-checked+.MuiSwitch-track': {
    backgroundColor: `var(--purple-700) !important`,
  },
};

const AvatarAndName = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderLabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export type NotificationSettingsProps = {
  /**
   * ID of the member whose settings should be fetched.
   */
  memberId: string;
  /**
   * Optional callback to be triggered on successful save of notification settings update.
   */
  onSaveSuccess?: () => void;
  /**
   * Optional callback to be triggered when the submission state changes.
   */
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export type NotificationSettingsHandle = {
  save: () => void;
  isSubmitting: boolean;
};

const NotificationSettings = React.forwardRef<NotificationSettingsHandle, NotificationSettingsProps>(
  ({ memberId, onSaveSuccess, onSubmittingChange }, ref) => {
    const authContext = React.useContext(AuthContext);
    const { data, error, isPending } = useQuery<NotificationData>({
      queryKey: ['notification-settings', memberId],
      queryFn: () => {
        return client.get(`notification/settings/${memberId}`).then((res) => res.data);
      },
      enabled: !!memberId,
    });
    const [memberSettings, setMemberSettings] = useState<NotificationSettingsType>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Also store memberSettings in local state so we can use conditional logic in the table
    useEffect(() => {
      if (data && data.member) {
        setMemberSettings(data.member);
      }
    }, [data]);

    // Notify parent when submission state changes
    useEffect(() => {
      onSubmittingChange?.(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
    const queryClient = useQueryClient();

    const submit = useMutation<unknown, AxiosError<ErrorResponse>, NotificationSettingsType>({
      mutationFn: async (data) => {
        if (isSubmitting) {
          return;
        }
        setIsSubmitting(true);

        if (isTrainingMode) {
          console.warn('Training Mode: Mocking save notification settings');
          queryClient.setQueryData<NotificationData>(['notification-settings', memberId], (old) => {
            if (!old) return old;
            return {
              ...old,
              member: {
                ...old.member,
                email_address: data.email_address || old.member.email_address,
                selected_npis_email: data.selected_npis_email || old.member.selected_npis_email,
                selected_npis_filters: data.selected_npis_filters || old.member.selected_npis_filters,
              },
            };
          });
          return Promise.resolve({ success: true });
        }
        // TODO: it'd be nice to check for changes from the existing state
        // and only send the diff
        return client.post(`notification/settings/${memberId}/save`, data).then((res) => res.data);
      },
      onSuccess: () => {
        toast.custom(() => <Toast variant="success">Settings saved</Toast>);

        if (authContext.user && data) {
          // Get providers that have filtering enabled
          const selectedNpisFilters = memberSettings.selected_npis_filters ?? [];
          const providersWithNames = data.providers
            .filter((provider) => selectedNpisFilters.includes(provider.npi))
            .map((provider) => ({
              npi: provider.npi,
              name: provider.name || provider.npi, // fallback to NPI if name not found
            }));

          applySavedFilters(authContext, providersWithNames, queryClient);
        }

        onSaveSuccess?.();
      },
      onError: () => {
        toast.custom(() => <Toast variant="error">Error saving settings</Toast>);
      },
      onSettled: () => {
        setIsSubmitting(false);
        queryClient.invalidateQueries({ queryKey: ['notification-settings', memberId] });
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        // This allows a parent component with its own submit button to trigger
        // submission and track the submission state
        save: () => submit.mutate(memberSettings),
        isSubmitting,
      }),
      [memberSettings, submit, isSubmitting],
    );

    if (isPending) {
      return <LoadingSpinner height="200px" />;
    } else if (error) {
      return <PageLoadError />;
    } else if (!data) {
      return null;
    }
    const selectedNpisEmail = memberSettings.selected_npis_email ?? [];
    const selectedNpisFilters = memberSettings.selected_npis_filters ?? [];

    return (
      <CustomTableContainer>
        <Table>
          <thead>
            <TableRow>
              <CustomTableCell>
                <Typography styledAs="bodyLargeDMSans" weight={600}>
                  Notifications by NPI/Prescriber
                </Typography>
              </CustomTableCell>
              <SwitchColumnHeaderCell>
                <HeaderLabelWithTooltip>
                  <Typography styledAs="bodyExtraSmallDMSans" weight={600}>
                    Filters
                  </Typography>
                  <Tooltip
                    content="Pre-set filters let you choose which prescribers you want to see by default when you log in. You can always change these filters on any page."
                    childNotInteractive
                    maxWidth="228px"
                  >
                    <InfoIcon aria-hidden />
                  </Tooltip>
                </HeaderLabelWithTooltip>
              </SwitchColumnHeaderCell>
              <SwitchColumnHeaderCell>
                <Typography styledAs="bodyExtraSmallDMSans" weight={600}>
                  Email
                </Typography>
              </SwitchColumnHeaderCell>
            </TableRow>
          </thead>
          <tbody>
            {data.providers.map((provider) => (
              <TableRow key={provider.npi}>
                <CustomTableCell>
                  <AvatarAndName>
                    <InitialsAvatar name={provider.name || '#'} />
                    {provider.name ? (
                      <div>
                        <Typography styledAs="bodySmallDMSans" weight={600}>
                          {provider.name}
                        </Typography>
                        <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                          NPI: {provider.npi}
                        </Typography>
                      </div>
                    ) : (
                      <Typography styledAs="bodySmallDMSans" weight={600}>
                        NPI: {provider.npi}
                      </Typography>
                    )}
                  </AvatarAndName>
                </CustomTableCell>
                <CustomTableCell>
                  <Switch
                    sx={switchStyling}
                    checked={selectedNpisFilters.includes(provider.npi)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const updatedSettings = {
                        ...memberSettings,
                        selected_npis_filters: e.target.checked
                          ? [...selectedNpisFilters, provider.npi]
                          : selectedNpisFilters.filter((npi: string) => npi !== provider.npi),
                      };
                      setMemberSettings(updatedSettings);
                    }}
                  />
                </CustomTableCell>
                <CustomTableCell>
                  <Switch
                    sx={switchStyling}
                    checked={selectedNpisEmail.includes(provider.npi)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const updatedSettings = {
                        ...memberSettings,
                        selected_npis_email: e.target.checked
                          ? [...selectedNpisEmail, provider.npi]
                          : selectedNpisEmail.filter((npi: string) => npi !== provider.npi),
                      };
                      setMemberSettings(updatedSettings);
                    }}
                  />
                </CustomTableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </CustomTableContainer>
    );
  },
);

export default NotificationSettings;
